<template>
  <router-link
    :to="jobUrl"
    class="group block min-w-[200px] sm:min-w-[240px] max-w-[240px] snap-center bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-lg shadow-[0_2px_15px_rgb(0,0,0,0.04)] dark:shadow-none hover:shadow-[0_8px_25px_rgb(0,0,0,0.08)] dark:hover:shadow-xl dark:hover:shadow-gray-900/50 p-3 transition-all shrink-0 flex flex-col hover:-translate-y-1"
  >
    <div class="flex items-start justify-between mb-2">
      <span
        class="bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 text-[10px] font-bold px-1.5 py-0.5 rounded tracking-wide uppercase"
        >{{ $t('home.hiring') }}</span
      >
      <button
        @click.prevent="handleBookmark"
        class="transition-colors"
        :class="
          isBookmarked(job.id)
            ? 'text-blue-600 dark:text-blue-400'
            : 'text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400'
        "
      >
        <Icon
          :icon="isBookmarked(job.id) ? 'mdi:bookmark' : 'mdi:bookmark-outline'"
          class="w-5 h-5"
        />
      </button>
    </div>

    <div class="flex items-center gap-2 mb-2">
      <div
        class="w-10 h-10 bg-white dark:bg-gray-700 rounded-lg shadow-sm border border-gray-100 dark:border-gray-600 flex items-center justify-center p-1 shrink-0"
      >
        <img
          v-if="job.logo_perusahaan"
          :src="job.logo_perusahaan"
          :alt="job.nama_perusahaan"
          class="w-full h-full object-contain"
          loading="lazy"
          decoding="async"
        />
        <span v-else class="text-lg font-bold text-[#293146] dark:text-gray-200">{{
          job.nama_perusahaan?.charAt(0)
        }}</span>
      </div>
      <div class="min-w-0">
        <h3
          class="font-bold text-base text-gray-900 dark:text-white leading-tight truncate group-hover:text-slate-700 dark:group-hover:text-[#C5B47A] transition-colors"
        >
          {{ job.judul_pekerjaan }}
        </h3>
        <p
          class="text-sm text-gray-600 dark:text-gray-300 font-medium truncate group-hover:text-slate-600 dark:group-hover:text-[#C5B47A] transition-colors"
        >
          {{ job.nama_perusahaan }}
        </p>
      </div>
    </div>

    <div class="space-y-1 text-sm text-gray-600 dark:text-gray-300 mb-2 flex-grow">
      <div class="flex items-center">
        <Icon
          icon="mdi:map-marker-outline"
          class="w-3.5 h-3.5 mr-1.5 text-gray-400 dark:text-gray-500 shrink-0"
        />
        <span class="truncate">{{ job.lokasi_penempatan }}</span>
      </div>
    </div>

    <div
      class="pt-2 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between mt-auto"
    >
      <span class="text-[11px] text-gray-400 dark:text-gray-500 flex items-center">
        <Icon icon="mdi:clock-outline" class="w-3.5 h-3.5 mr-1" />
        {{ formatTimeAgo(job.created_at) }}
      </span>
      <span
        class="bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider"
        >{{ job.tipe_pekerjaan }}</span
      >
    </div>
  </router-link>
</template>

<script setup>
import { computed } from 'vue';
import { Icon } from '@iconify/vue';
import { useI18n } from 'vue-i18n';
import { useBookmarks } from '../../composables/useBookmarks';

const props = defineProps({
  job: {
    type: Object,
    required: true,
  },
});

const { t, locale } = useI18n();
const { isBookmarked, toggleBookmark } = useBookmarks();

const jobUrl = computed(() => {
  const slug = props.job.slug || props.job.id;
  return `/jobs/${slug}`;
});

const handleBookmark = () => {
  toggleBookmark(props.job);
};

const formatTimeAgo = (date) => {
  if (!date) return t('job.time.justNow');
  const now = new Date();
  const past = new Date(date);
  const diffInSeconds = Math.floor((now - past) / 1000);

  if (diffInSeconds < 60) return t('job.time.justNow');

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return t('job.time.minutesAgo', { count: diffInMinutes });

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return t('job.time.hoursAgo', { count: diffInHours });

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) return t('job.time.daysAgo', { count: diffInDays });

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) return t('job.time.monthsAgo', { count: diffInMonths });

  return past.toLocaleDateString(locale.value === 'id' ? 'id-ID' : 'en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};
</script>
